import app from 'flarum/forum/app';

/**
 * Client-side nudity screening for story images.
 *
 * The model runs in the visitor's browser, so nothing is uploaded before it is
 * judged and the server does no work. That also means it can be bypassed by
 * anyone posting straight to the API — this is a deterrent and a courtesy to
 * the user, not an access control. Treat it accordingly.
 *
 * TensorFlow and the model are pulled from a CDN on first use rather than
 * bundled: together they are tens of megabytes, and most visitors never open
 * the story composer at all.
 */

const TF_URL = 'https://cdn.jsdelivr.net/npm/@tensorflow/tfjs@4.22.0/dist/tf.min.js';
// The package's browser build, as named by its own `unpkg` field — the ESM
// entry at dist/ is not loadable with a plain <script> tag.
const NSFW_URL = 'https://cdn.jsdelivr.net/npm/nsfwjs@4.4.0/dist/browser/nsfwjs.min.js';

/** Classes that count against an image, and how much each is tolerated. */
const LIMITS = {
  Porn: 0.6,
  Hentai: 0.6,
  Sexy: 0.8,
};

let modelPromise = null;

/**
 * Whether screening is switched on for this forum.
 *
 * @returns {boolean}
 */
export function nsfwEnabled() {
  return !!app.forum.attribute('happydigital-stories.nsfwCheck');
}

/**
 * Check an image file.
 *
 * Resolves to `{ blocked, reason }`. A failure to load the model or to read
 * the image resolves as not blocked: a broken CDN must not stop people from
 * posting, and a silent permanent failure would be worse than no screening.
 *
 * @param {File} file
 * @returns {Promise<{blocked: boolean, reason: ?string}>}
 */
export async function checkImage(file) {
  if (!nsfwEnabled()) return { blocked: false, reason: null };

  try {
    const model = await loadModel();
    const image = await toImageElement(file);

    const predictions = await model.classify(image);

    URL.revokeObjectURL(image.src);

    // Opt-in scores, so thresholds can be tuned against real images:
    //   localStorage.setItem('happyStoriesNsfwDebug', '1')
    try {
      if (localStorage.getItem('happyStoriesNsfwDebug') === '1') {
        console.table(
          predictions.map((p) => ({
            sınıf: p.className,
            skor: p.probability.toFixed(3),
            eşik: LIMITS[p.className] === undefined ? '—' : LIMITS[p.className],
          }))
        );
      }
    } catch (e) {
      // Storage unavailable; scores just stay hidden.
    }

    for (const { className, probability } of predictions) {
      const limit = LIMITS[className];

      if (limit !== undefined && probability >= limit) {
        return { blocked: true, reason: className };
      }
    }

    return { blocked: false, reason: null };
  } catch (e) {
    // Model unavailable, image unreadable, WebGL blocked — let the upload
    // through rather than locking the composer over a screening failure.
    return { blocked: false, reason: null };
  }
}

/** Load TensorFlow and the model once, sharing the work across callers. */
function loadModel() {
  if (!modelPromise) {
    modelPromise = (async () => {
      await loadScript(TF_URL);
      await loadScript(NSFW_URL);

      // Named explicitly: leaving it out makes the library log a notice on
      // every load about which default it picked.
      return window.nsfwjs.load('MobileNetV2');
    })().catch((e) => {
      // Let the next attempt retry instead of caching the failure forever.
      modelPromise = null;
      throw e;
    });
  }

  return modelPromise;
}

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }

    const script = document.createElement('script');

    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('script yüklenemedi: ' + src));

    document.head.appendChild(script);
  });
}

function toImageElement(file) {
  return new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('resim okunamadı'));
    image.src = URL.createObjectURL(file);
  });
}
